// src/homepage/BlogSection.tsx
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const Section = styled.section`
    padding: 4rem 0;
    background: #ffffff;
    position: relative;
    min-height: auto;
    display: flex;
    align-items: center;

    @media (max-width: 768px) {
        padding: 3rem 0;
    }

    @media (max-width: 480px) {
        padding: 2rem 0;
    }
`;

const Container = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;

    @media (max-width: 768px) {
        padding: 0 2rem;
    }

    @media (max-width: 480px) {
        padding: 0 2rem;
    }
    padding-bottom: 150px;
`;

const SectionHeader = styled.div`
    text-align: center;
    margin-bottom: 3rem;

    @media (max-width: 768px) {
        margin-bottom: 2rem;
    }
`;

const SectionTitle = styled(motion.h2)`
    font-size: 2.8rem;
    font-weight: 800;
    color: #000000;
    line-height: 1.1;
    margin-bottom: 1rem;

    @media (max-width: 1024px) {
        font-size: 2.4rem;
    }

    @media (max-width: 768px) {
        font-size: 2rem;
        margin-bottom: 0.75rem;
    }

    @media (max-width: 480px) {
        font-size: 1.75rem;
    }
`;

const SectionSubtitle = styled(motion.p)`
    font-size: 1.1rem;
    color: #4a5568;
    line-height: 1.6;
    max-width: 600px;
    margin: 0 auto;

    @media (max-width: 768px) {
        font-size: 1rem;
        line-height: 1.5;
        padding: 0 1rem;
    }

    @media (max-width: 480px) {
        font-size: 0.95rem;
        padding: 0;
    }
`;

const BlogGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;

    @media (max-width: 1024px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 1.5rem;
    }

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
        gap: 1.5rem;
        max-width: 400px;
        margin: 0 auto;
    }
`;

const BlogCard = styled(motion.article)`
    background: #ffffff;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;
    display: flex;
    flex-direction: column;
    height: 100%;

    &:hover {
        box-shadow: 0 8px 30px rgba(153, 1, 71, 0.15);
        transform: translateY(-5px);
    }

    @media (max-width: 640px) {
        border-radius: 12px;
    }
`;

const ImageWrapper = styled.div`
    position: relative;
    width: 100%;
    height: 220px;
    overflow: hidden;

    @media (max-width: 1024px) {
        height: 200px;
    }

    @media (max-width: 768px) {
        height: 180px;
    }

    @media (max-width: 480px) {
        height: 160px;
    }
`;

const BlogImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;

    ${BlogCard}:hover & {
        transform: scale(1.05);
    }
`;

const CategoryTag = styled.span`
    position: absolute;
    top: 1rem;
    left: 1rem;
    padding: 0.4rem 1rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 700;
    color: #ffffff;
    background: linear-gradient(135deg, #990147 0%, #cc3366 100%);
    text-transform: uppercase;
    letter-spacing: 0.5px;

    @media (max-width: 480px) {
        top: 0.75rem;
        left: 0.75rem;
        padding: 0.3rem 0.8rem;
        font-size: 0.7rem;
    }
`;

const BlogContent = styled.div`
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    flex-grow: 1;

    @media (max-width: 768px) {
        padding: 1.25rem;
    }

    @media (max-width: 480px) {
        padding: 1rem;
    }
`;

const BlogTitle = styled.h3`
    font-size: 1.25rem;
    font-weight: 700;
    color: #000000;
    margin-bottom: 0.75rem;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;

    @media (max-width: 768px) {
        font-size: 1.1rem;
        margin-bottom: 0.5rem;
    }

    @media (max-width: 480px) {
        font-size: 1rem;
    }
`;

const BlogExcerpt = styled.p`
    font-size: 0.95rem;
    color: #4a5568;
    line-height: 1.5;
    margin-bottom: 1.25rem;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    flex-grow: 1;

    @media (max-width: 768px) {
        font-size: 0.9rem;
        margin-bottom: 1rem;
        -webkit-line-clamp: 2;
    }

    @media (max-width: 480px) {
        font-size: 0.85rem;
    }
`;

const BlogMeta = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.25rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(0, 0, 0, 0.1);

    @media (max-width: 768px) {
        margin-bottom: 1rem;
        padding-top: 0.75rem;
    }
`;

const MetaItem = styled.span`
    background: #f8f9fa;
    padding: 0.4rem 0.8rem;
    border-radius: 12px;
    font-size: 0.8rem;
    color: #4a5568;
    font-weight: 500;

    @media (max-width: 480px) {
        font-size: 0.75rem;
        padding: 0.3rem 0.6rem;
    }
`;

const ReadMoreButton = styled(motion.a)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.75rem 1.5rem;
    background: linear-gradient(135deg, #990147 0%, #cc3366 100%);
    color: #ffffff;
    border-radius: 10px;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.3s ease;
    font-size: 0.9rem;
    border: none;
    cursor: pointer;
    width: 100%;

    &:hover {
        box-shadow: 0 4px 15px rgba(153, 1, 71, 0.3);
        transform: translateY(-2px);
    }

    @media (max-width: 768px) {
        padding: 0.65rem 1.25rem;
        font-size: 0.85rem;
    }

    @media (max-width: 480px) {
        padding: 0.6rem 1rem;
        font-size: 0.8rem;
    }
`;

interface BlogPost {
    id: number;
    category: string;
    image: string;
    title: string;
    excerpt: string;
    date: string;
    readTime: string;
    link: string;
}

const BlogSection: React.FC = () => {
    const blogPosts: BlogPost[] = [
        {
            id: 1,
            category: "Duševné zdravie",
            image: "/img_2.png",
            title: "Duševné zdravie – prečo a ako sa oň starať?",
            excerpt: "10 dobrých tipov, ako sa starať o svoje duševné zdravie a byť psychicky v pohode.",
            date: "10. november 2025",
            readTime: "5 min",
            link: "https://vegmart.sk/dusevne-zdravie?srsltid=AfmBOooU9FhFJhe2K1XBrepFukaHIEwPCqu-TgatXdjzCikBqR6zwxxZ"
        },
        {
            id: 2,
            category: "Výživa",
            title: "Strava a psychika: Jedlo, ktoré zlepšuje náladu",
            image: "/img_3.png",
            excerpt: "Viete o tom, že potraviny, ktoré zjete, sa automaticky prejavia aj na vašej nálade či psychickom stave?",
            date: "5. november 2025",
            readTime: "7 min",
            link: "https://www.union.sk/zdrava-strava-a-psychika/"
        },
        {
            id: 3,
            category: "Spánok",
            title: "7 tipov ako dosiahnuť kvalitný spánok",
            image: "/img_4.png",
            excerpt: "Zistite, ako zlepšiť kvalitu spánku pomocou hygieny spánku a rutín, ktoré vám pomôžu oddýchnuť si lepšie.",
            date: "1. november 2025",
            readTime: "6 min",
            link: "https://www.zdravotne-matrace.sk/blog/7-tipov-ako-dosiahnut-kvalitny-spanok/"
        }
    ];

    return (
        <Section id="blog">
            <Container>
                <SectionHeader>
                    <SectionTitle
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true, margin: '-50px' }}
                    >
                        Najnovšie Články
                    </SectionTitle>
                    <SectionSubtitle
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        viewport={{ once: true, margin: '-50px' }}
                    >
                        Objavte naše najnovšie články o duševnom zdraví, výžive a životnom štýle. Každý článok je navrhnutý tak, aby vám poskytol užitočné rady a inšpiráciu.
                    </SectionSubtitle>
                </SectionHeader>

                <BlogGrid>
                    {blogPosts.map((post, index) => (
                        <BlogCard
                            key={post.id}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: index * 0.1 }}
                            viewport={{ once: true, margin: '-50px' }}
                            whileHover={{ y: -5 }}
                        >
                            <ImageWrapper>
                                <BlogImage
                                    src={post.image}
                                    alt={post.title}
                                    loading="lazy"
                                />
                                <CategoryTag>{post.category}</CategoryTag>
                            </ImageWrapper>
                            <BlogContent>
                                <BlogTitle>{post.title}</BlogTitle>
                                <BlogExcerpt>{post.excerpt}</BlogExcerpt>
                                <BlogMeta>
                                    <MetaItem>{post.date}</MetaItem>
                                    <MetaItem>{post.readTime}</MetaItem>
                                </BlogMeta>
                                <ReadMoreButton
                                    href={post.link}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    Čítať Viac
                                </ReadMoreButton>
                            </BlogContent>
                        </BlogCard>
                    ))}
                </BlogGrid>
            </Container>
        </Section>
    );
};

export default BlogSection;